export type DetectedOs = 'ios' | 'android' | 'windows' | 'macos' | 'linux' | 'other';

// Answers "which store can this visitor install from?" as well as feeding the
// analytics `os` super property, so both read the same sniff.
export const detectOs = (): DetectedOs => {
  const ua = navigator.userAgent.toLowerCase();
  // iPadOS Safari reports itself as a Mac; the touch check separates them.
  const isIpadOs = ua.includes('mac') && navigator.maxTouchPoints > 1;
  if (/iphone|ipad|ipod/.test(ua) || isIpadOs) return 'ios';
  if (ua.includes('android')) return 'android';
  if (ua.includes('windows')) return 'windows';
  if (ua.includes('mac')) return 'macos';
  if (ua.includes('linux')) return 'linux';
  return 'other';
};

export type InAppBrowser = 'instagram' | 'facebook';

// Meta's in-app browsers, which Mixpanel's $browser can't tell apart from the
// real ones: Instagram on iOS reads as "Mobile Safari", Facebook on Android as
// "Chrome". Instagram is checked first since its UA can also carry FB tokens.
export const detectInAppBrowser = (ua: string = navigator.userAgent): InAppBrowser | null => {
  if (/\bInstagram\b/.test(ua)) return 'instagram';
  if (/\bFBAN\/|\bFBAV\/|\bFB_IAB\//.test(ua)) return 'facebook';
  return null;
};

// Browsers freeze these UA versions (Chrome's reduced UA reports every Android
// as "Android 10; K", every Mac as 10_15_7, and Windows 11 as NT 10.0), so a
// match means "unknown", not that version.
const FROZEN_UA_VERSION = /Android 10; K\b|Mac OS X 10_15_7|Windows NT 10\.0/;

/** OS version from the user agent, or undefined when the UA doesn't say or is frozen. */
export const detectOsVersion = (ua: string = navigator.userAgent): string | undefined => {
  if (FROZEN_UA_VERSION.test(ua)) return undefined;
  const match =
    /(?:iPhone|CPU) OS (\d+(?:_\d+)*)/.exec(ua) ??
    /Android (\d+(?:\.\d+)*)/.exec(ua) ??
    /Mac OS X (\d+(?:_\d+)*)/.exec(ua) ??
    /Windows NT (\d+\.\d+)/.exec(ua);
  return match?.[1].replace(/_/g, '.');
};

/**
 * Normalizes a User-Agent Client Hints `platformVersion`. Windows reports a
 * build-derived number (13+ is Windows 11); other platforms report the real
 * version, trimmed of trailing zero segments.
 */
export const formatPlatformVersion = (
  platform: string | undefined,
  platformVersion: string
): string | undefined => {
  if (platform === 'Windows') {
    const major = parseInt(platformVersion, 10);
    if (major >= 13) return '11';
    return major > 0 ? '10' : undefined;
  }
  return platformVersion.replace(/(\.0)+$/, '') || undefined;
};

type UserAgentDataLike = {
  getHighEntropyValues?: (
    hints: string[]
  ) => Promise<{ platform?: string; platformVersion?: string }>;
};

let osVersionPromise: Promise<string | undefined> | undefined;

/**
 * The real OS version where Client Hints expose it (Chromium), else the UA
 * value. Resolved once per page load.
 */
export const getOsVersion = (): Promise<string | undefined> => {
  osVersionPromise ??= (async () => {
    const uaData = (navigator as Navigator & { userAgentData?: UserAgentDataLike }).userAgentData;
    try {
      const hints = await uaData?.getHighEntropyValues?.(['platformVersion']);
      if (hints?.platformVersion)
        return formatPlatformVersion(hints.platform, hints.platformVersion);
    } catch {
      // Hints can be denied by permissions policy; the UA fallback still applies.
    }
    return detectOsVersion();
  })();
  return osVersionPromise;
};

export type DetectedBrowser = { name: string; version?: string };

// Ordered: Edge, Opera and Samsung UAs also carry a Chrome token, and every
// iOS browser carries Safari's.
const BROWSER_PATTERNS: [string, RegExp][] = [
  ['edge', /Edg(?:A|iOS)?\/([\d.]+)/],
  ['opera', /OPR\/([\d.]+)/],
  ['samsung', /SamsungBrowser\/([\d.]+)/],
  ['firefox', /(?:Firefox|FxiOS)\/([\d.]+)/],
  ['chrome', /(?:Chrome|CriOS)\/([\d.]+)/],
  ['safari', /Version\/([\d.]+).*Safari/],
];

/** Browser name and major.minor version from the user agent. */
export const detectBrowser = (ua: string = navigator.userAgent): DetectedBrowser => {
  for (const [name, pattern] of BROWSER_PATTERNS) {
    const match = pattern.exec(ua);
    if (match) return { name, version: match[1].split('.').slice(0, 2).join('.') };
  }
  return { name: 'other' };
};
